import { dataLayerConfig, TAGLink, useFacilityContext } from '@aspendental/shared-components-web';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { Box, Button, CircularProgress, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { usePathname } from 'next/navigation';
import router from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FOUR_STEPS, THREE_STEPS } from './ScheduleLayout.helpers';
import TermsAndConditions from './TermsAndConditions';

import Confirmation from '@src/components/Confirmation';
import {
	mapDentalConditionCodes,
	mapDentalSolutionCodes,
} from '@src/components/Forms/PatientInformation/PatientInformation.helpers';
import FurtherAssistanceContact from '@src/components/FurtherAssistanceContact';
import LoadingModal from '@src/components/LoadingSpinnerModal';
import PatientInformationStep from '@src/components/PatientInformationStep';
import PromptToCallModal from '@src/components/PromptToCallModal';
import QuestionnaireStep from '@src/components/QuestionnaireStep';
import ScheduleInfoModal from '@src/components/ScheduleInfoModal/ScheduleInfoModal';
import ScheduleStepProgress from '@src/components/ScheduleStepProgress';
import TimeSlotSelectionStep from '@src/components/TimeSlotSelectionStep';
import { FormIdMappings, SubmitAppointmentErrorCode } from '@src/constants';
import { getFormSectionQuestions } from '@src/services/getFormQuestions';
import { getLeadsScore } from '@src/services/getLeadsScore';
import { submitAppointment } from '@src/services/submitAppointment';
import { updateLead } from '@src/services/updateLead';
import { useAppStore, usePatientInformationStore, useStepManagerStore } from '@src/store';
import useAppointmentInformationStore from '@src/store/appointment-information.store';
import useScheduleQuestionsStore from '@src/store/schedule-questions.store';
import marketingSourceUrlParse from '@src/utils/marketing-source-url-parse';

interface IScheduleLayoutProps {
	smartyKey: string;
	baseUrl: string;
	enablePtcSlotFilter: boolean;
}

function ScheduleLayout({ smartyKey, baseUrl, enablePtcSlotFilter }: Readonly<IScheduleLayoutProps>) {
	const facilityDetails = useFacilityContext();
	const { t } = useTranslation('index');
	useEffect(() => {
		marketingSourceUrlParse();
	}, []);
	const pathname = usePathname();
	const isPageTwo = pathname === '/2/';
	const { locale } = router;
	const languagePreference = locale === 'es-US' ? 'Spanish' : 'English';

	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
	const steps = isSmallScreen ? FOUR_STEPS : THREE_STEPS;

	const [questions, setQuestions] = useState<any[]>([]);
	const [unansweredQuestions, setUnansweredQuestions] = useState<any[]>([]);
	const [schedulePopupError, setSchedulePopupError] = useState<boolean>(false);
	const [formId, setFormId] = useState<string>('step-1-form');
	const [noChosenAppointmentError, setNoChosenAppointmentError] = useState<boolean>(false);
	const [scheduleInfoModal, setScheduleInfoModal] = useState<{
		title: string;
		description: any;
		buttonText: string;
		buttonOnClick: any;
	} | null>(null);
	const [isStepSubmitting, setIsStepSubmitting] = useState<boolean>(false);
	const [loadingAppointments, setLoadingAppointments] = useState<boolean>(true);
	const [modalType, setModalType] = useState<'promptToCall' | 'existingConsultation'>('promptToCall');
	const questionsAnswers = useScheduleQuestionsStore((s) => s.questionsAnswers);
	const setQuestionData = useScheduleQuestionsStore((s) => s.setQuestionData);

	// Lead ID:
	const { leadId, firstName, salesforceS20, salesforceGuid, salesforceTier, setSalesforceS20, setSalesforceTier } =
		usePatientInformationStore();
	const {
		chosenCenterData,
		chosenAppointmentData,
		chosenAppointmentKey,
		presetCenterID,
		presetCenterName,
		setPresetCenterID,
		setPresetCenterName,
		trackAppoinmtentTimesShown,
		trackAppoinmtentDaysShown,
	} = useAppointmentInformationStore();
	const { setLeadFormId } = useAppStore((store) => store);

	const { currentStep, incrementStep, decrementStep } = useStepManagerStore((store) => store);

	useEffect(() => {
		// keep initial facility in state for tracking purposes
		if (facilityDetails.officeInfo && !presetCenterName && !presetCenterID) {
			setPresetCenterID(facilityDetails.officeInfo.code);
			setPresetCenterName(facilityDetails.officeInfo.name);
		}
	}, [facilityDetails, setPresetCenterID, setPresetCenterName, presetCenterName, presetCenterID]);

	useEffect(() => {
		const leadFormId = isPageTwo ? FormIdMappings.formID3DentalQuestions : FormIdMappings.formID;
		setLeadFormId(leadFormId);
		getFormSectionQuestions({
			form: leadFormId,
			locale: locale,
		}).then((data) => {
			setQuestionData(data?.form?.sections[0]?.questions);
			setQuestions(data?.form?.sections[0]?.questions);
		});
	}, [setQuestionData, isPageTwo, setLeadFormId, locale]);

	const mainContentRef = useRef<HTMLElement>(null);

	useEffect(() => {
		if (mainContentRef?.current) {
			const firstFocusableElement = mainContentRef.current.querySelectorAll<HTMLElement>(
				'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
			);
			firstFocusableElement[0]?.focus();
		}
		setNoChosenAppointmentError(false);
		setFormId(`step-${currentStep}-form`);
	}, [currentStep]);

	useEffect(() => {
		setNoChosenAppointmentError(false);
	}, [chosenAppointmentData]);

	const handleScrollTop = useCallback(() => {
		if (typeof window === 'undefined') return;
		if (isSmallScreen) {
			// Scroll to the top of the current step - 78 is the height of the step, 20 is padding top of the box
			window.scrollTo({ top: (currentStep - 1) * 78 + 20, behavior: 'smooth' });
		} else {
			window.scrollTo({ top: 0, behavior: 'smooth' });
		}
	}, [currentStep, isSmallScreen]);

	const handleBackStep = () => {
		if (isStepSubmitting) return;
		dataLayerConfig({ event: `scheduleform_step${currentStep}_clickback` });
		decrementStep();
		handleScrollTop();
	};

	const handleNextStep = () => {
		if (isStepSubmitting) return;

		if (currentStep === 1) {
			// Check if all required questions have been answered
			const unanswered = questions?.filter((question) => {
				if (question.required === 1 || question.required === '1') {
					const answers = questionsAnswers[question.short_name];
					return !answers || answers.length === 0;
				}
				return false;
			});

			setUnansweredQuestions(unanswered);

			if (unanswered?.length > 0) {
				handleScrollTop();
				return;
			}
			dataLayerConfig({ event: 'scheduleform_step1_clickcontinue' });

			const dentalConditionCodes = mapDentalConditionCodes(questionsAnswers.current_dental_condition);
			const dentalSolutionCodes = mapDentalSolutionCodes(questionsAnswers.current_dental_solutions);

			dataLayerConfig({
				event: 'dental_condition_form',
				lead_id: null,
				Q1_current_condition_code: dentalConditionCodes.join(', '),
				Q2_current_dental_solution_code: dentalSolutionCodes.join(', '),
				lead_tier: salesforceS20 && salesforceS20 != 21 ? salesforceTier : 'T1',
				lead_segment: salesforceS20 ?? '21',
				website_selected_center_name: presetCenterName,
				website_selected_center_id: presetCenterID,
				language_preference: locale ?? 'en-US',
			});

			incrementStep();
			setIsStepSubmitting(false);
			handleScrollTop();
			return;
		}

		if (currentStep === 2) {
			dataLayerConfig({ event: 'scheduleform_step2_clickcontinue' });
			setIsStepSubmitting(false);
			handleScrollTop();
			return;
		}

		if (currentStep === 3) {
			if (!chosenAppointmentData) {
				setNoChosenAppointmentError(true);
				handleScrollTop();
				return;
			} else {
				let updatedSegment = salesforceS20;
				let updatedTier = salesforceTier;
				setIsStepSubmitting(true);
				setTimeout(() => {
					// GA4: 2.4
					getLeadsScore({ leadId, salesforceId: salesforceGuid ?? '' })
						.then((data) => {
							if (data?.score?.lead_segment20) {
								setSalesforceS20(data.score.lead_segment20);
								updatedSegment = data.score.lead_segment20;
							}
							if (data?.score?.tier) {
								setSalesforceTier(`T${data.score.tier}`);
								updatedTier = `T${data.score.tier}`;
							}
						})
						.catch(async (error) => {
							console.error('Error getting Leads Score:', error);
						})
						.finally(() => {
							dataLayerConfig({
								event: 'appointment_step',
								step_name: 'location_date_time',
								lead_id: salesforceGuid,
								lead_tier: updatedSegment && updatedSegment != 21 ? updatedTier : 'T1',
								lead_segment: updatedSegment ?? '21',
								appointments_available: true, // < Always true if this block is hit
								website_selected_center_name: presetCenterName,
								website_selected_center_id: presetCenterID,
								appointment_selected_center_name: chosenCenterData?.name,
								appointment_selected_center_id: chosenCenterData?.code,
								language_preference: locale ?? 'en-US',
								ptc: enablePtcSlotFilter ? '1' : '0',
								appointment_time_slots: trackAppoinmtentTimesShown,
								appointment_days_shown: trackAppoinmtentDaysShown,
							});
							scheduleAppointment({
								dataLayerEvent: 'scheduleform_step3_confirmconsultation',
							});
						});
				}, 2500);
				return;
			}
		}
	};

	const scheduleAppointment = ({ dataLayerEvent }) => {
		updateLead({ leadId, language: languagePreference });
		dataLayerConfig({ event: dataLayerEvent });
		submitAppointment({ consultId: chosenAppointmentKey!, leadId })
			.then((data) => {
				if (data) {
					incrementStep();
				}
				setIsStepSubmitting(false);
				// Clear the persisted localStorage
				usePatientInformationStore.persist.clearStorage();
				handleScrollTop();
				return;
			})
			.catch(async (error) => {
				console.error('Error submitting appointment:', error);
				if (error.message === SubmitAppointmentErrorCode.CONSULT_SCHEDULED) {
					setScheduleInfoModal({
						title: 'It looks like you already have a consultation scheduled.',
						description: () => (
							<Typography variant="bodyLargeBook">
								{t('wantToCancelOrReschedule')} <br />
								{t('pleaseCallAt')}
								<TAGLink
									href={`tel:888-651-9950`}
									linkSx={{ color: theme.palette.text.interactive, textDecoration: 'none' }}
								>
									{' '}
									888-651-9950{' '}
								</TAGLink>
								{t('forAssistance')}
							</Typography>
						),
						buttonText: 'Cancel',
						buttonOnClick: () => {
							setScheduleInfoModal(null);
						},
					});
					setIsStepSubmitting(false);
					return;
				} else if (error.message === SubmitAppointmentErrorCode.NO_APPOINTMENTS) {
					setScheduleInfoModal({
						title: 'The appointment you have selected is no longer available.',
						description: () => (
							<Typography variant="bodyLargeBook">{t('chooseAnotherTimeApology')}</Typography>
						),
						buttonText: 'Cancel',
						buttonOnClick: () => {
							setScheduleInfoModal(null);
						},
					});
					setIsStepSubmitting(false);
					return;
				}
				setIsStepSubmitting(false);
				setSchedulePopupError(true);
				handleScrollTop();
				return;
			});
	};

	const stepsData = useMemo(() => {
		const previousSteps = steps.filter((step) => step.step < currentStep);
		const currentStepData = steps.find((step) => step.step === currentStep);
		const nextSteps = steps.filter((step) => step.step > currentStep);

		return {
			previousSteps,
			currentStep: currentStepData,
			nextSteps,
		};
	}, [currentStep, steps]);

	const stepTitle = (() => {
		if (currentStep === 4) {
			return `${t('allSet')} ${firstName}!`;
		}

		return t(steps[currentStep - 1]?.titleKey);
	})();

	return (
		<>
			{currentStep === 2 && isStepSubmitting && (
				<LoadingModal
					open={isStepSubmitting}
					message={t('pleaseWait')}
					dataTestId="loading-modal-please-wait"
				/>
			)}
			{currentStep === 3 && loadingAppointments && (
				<LoadingModal
					open={loadingAppointments}
					message={t('retrievingAppointments')}
					dataTestId="loading-modal-retrieving-appointments"
				/>
			)}

			<PromptToCallModal
				open={schedulePopupError}
				handleClose={() => setSchedulePopupError(false)}
				modalType={modalType}
			/>
			{Boolean(scheduleInfoModal) && (
				<ScheduleInfoModal
					open={Boolean(scheduleInfoModal)}
					handleClose={() => setScheduleInfoModal(null)}
					{...scheduleInfoModal}
				/>
			)}
			<Stack
				direction="row"
				p={{ md: 5, xs: 0, xl: '2% 15%', l: '2% 10%' }}
				bgcolor="backgrounds.neutral"
				spacing={2.5}
				justifyContent="center"
			>
				<Stack direction="row" spacing={2.5} width="100%" maxWidth="75rem">
					{!isSmallScreen && (
						<Box
							width={275}
							minWidth={275}
							bgcolor="backgrounds.white"
							py={1.5}
							borderRadius={2}
							sx={{
								borderWidth: 1,
								borderStyle: 'solid',
								borderColor: 'neutrals.primary',
								height: 'max-content',
							}}
						>
							{steps.map(({ step, titleKey }, index) => {
								const selected = step === currentStep;
								const completed = step < currentStep;

								if (index < 3) {
									return (
										<ScheduleStepProgress
											key={step}
											step={step}
											stepTitle={titleKey}
											selected={selected}
											completed={completed}
											disabled={!completed || currentStep === steps.length}
										/>
									);
								}
							})}
						</Box>
					)}

					<Box width="100%" ref={mainContentRef}>
						<Box
							width="100%"
							bgcolor="#FFFFFF"
							p={{ md: 5, xs: 2.5 }}
							borderRadius={2}
							sx={{
								borderWidth: 1,
								borderStyle: 'solid',
								borderColor: 'neutrals.primary',
							}}
						>
							<Stack direction="column" spacing={1}>
								{isSmallScreen && (
									<Stack direction="column">
										{/* Mobile previous steps */}
										{stepsData.previousSteps.map(({ step, titleKey }) => {
											const completed = step < currentStep;

											return (
												<Box key={step}>
													<ScheduleStepProgress
														key={step}
														step={step}
														stepTitle={titleKey}
														completed={true}
														disabled={!completed || currentStep === steps.length}
													/>
													<Box
														sx={{
															height: '32px',
															width: '1px',
															backgroundColor: 'neutrals.primary',
															ml: '12px',
														}}
													/>
												</Box>
											)
										})}
										{/* Mobile current step */}
										<ScheduleStepProgress
											step={currentStep}
											stepTitle={stepTitle}
											selected={true}
										/>
									</Stack>
								)}
								{!isSmallScreen && (
									<Typography
										variant="header2"
										data-test-id="schedule-layout-title"
										pt={{ md: 0, xs: 1.75 }}
										{...(currentStep === 4
											? {
													'data-private': true,
											  }
											: {})}
										pb={1}
									>
										{currentStep < 4
											? t(steps[currentStep - 1].stepTitleKey)
											: t(steps[currentStep - 1].stepTitleKey) + ' ' + firstName + '!'}
									</Typography>
								)}
								{steps[currentStep - 1]?.subtitleKey && (
									<Typography variant="bodyLargeBook" data-test-id="schedule-layout-subtitle">
										{t(steps[currentStep - 1].subtitleKey)}
									</Typography>
								)}

								<Box>
									{currentStep === 1 && (
										<QuestionnaireStep
											questions={questions}
											unansweredQuestions={unansweredQuestions}
										/>
									)}
									{currentStep === 2 && (
										<PatientInformationStep
											smartyKey={smartyKey}
											isStepSubmitting={isStepSubmitting}
											setIsStepSubmitting={setIsStepSubmitting}
											setSchedulePopupError={setSchedulePopupError}
											setModalType={setModalType}
										/>
									)}
									{currentStep === 3 && (
										<TimeSlotSelectionStep
											leadId={leadId}
											setIsStepSubmitting={setIsStepSubmitting}
											setSchedulePopupError={setSchedulePopupError}
											setModalType={setModalType}
											loadingAppointments={loadingAppointments}
											setLoadingAppointments={setLoadingAppointments}
											enablePtcSlotFilter={enablePtcSlotFilter}
										/>
									)}
									{currentStep === 4 && <Confirmation enablePtcSlotFilter={enablePtcSlotFilter} />}
								</Box>

								{isSmallScreen && (
									<Stack direction="column">
										{/* Mobile next steps */}
										{stepsData.nextSteps.map(({ step, titleKey }, index) => (
											<Box key={step}>
												<ScheduleStepProgress step={step} stepTitle={titleKey} />
												{index !== stepsData.nextSteps.length - 1 && (
													<Box
														sx={{
															height: '32px',
															width: '1px',
															backgroundColor: 'neutrals.primary',
															ml: '12px',
														}}
													/>
												)}
											</Box>
										))}
									</Stack>
								)}
							</Stack>
						</Box>

						{!isSmallScreen && (
							<Box mt={3.5}>
								<FurtherAssistanceContact />
							</Box>
						)}
					</Box>
				</Stack>
			</Stack>

			{currentStep < 4 && (
				<Stack
					py={2.5}
					alignItems="center"
					justifyContent="center"
					sx={{
						boxShadow: 'rgba(0, 0, 0, 0.1) 0px -1px 4px',
						position: 'sticky',
						bottom: 0,
						zIndex: 20,
						backgroundColor: 'backgrounds.white',
					}}
				>
					{noChosenAppointmentError && (
						<Typography variant="bodyMediumBook" color="error.dark">
							{t('chooseAnotherTime')}
						</Typography>
					)}
					{currentStep === 3 && <TermsAndConditions baseUrl={baseUrl} />}

					<Stack direction="row" spacing={1.5} alignItems="center">
						{currentStep > 1 && (
							<Button size="M" variant="text" onClick={handleBackStep}>
								<ChevronLeftIcon color="primary" />
								<Typography color="text.primary" variant="bodyLargeBook">
									{t('back')}
								</Typography>
							</Button>
						)}
						<Button
							form={formId}
							type="submit"
							variant="primaryAccent1"
							data-test-id="schedule-layout-button"
							size="M"
							onClick={handleNextStep}
							id={formId}
						>
							{isStepSubmitting && <CircularProgress sx={{ mr: 1 }} size={20} />}
							{isStepSubmitting
								? t('loading')
								: currentStep === 3
								? t('confirmConsultation')
								: t('continue')}
						</Button>
					</Stack>
				</Stack>
			)}
			{isSmallScreen && (
				<Box my={2.5} sx={{ textAlign: 'center' }}>
					<FurtherAssistanceContact />
				</Box>
			)}
		</>
	);
}

export default ScheduleLayout;
